/* eslint-disable no-restricted-globals */

import {
  getMeristemContext,
  getRouteId,
  setContextType,
} from "src/utils/meristemContext";
import getStore from "src/utils/redux/store";
import { captureException } from "src/utils/error";
import { createForwardParams, getSearchQuery } from "src/utils/urlParams";
import { isHM, isInApp } from "src/utils/userSegment";
import {
  isDevMode,
  isInAppNativePurchaseEligible,
} from "src/utils/userSegment/features";
import goto from ".";
import { getPageSetForLayer } from "../pageSets";
import {
  isInsideAndroidWebView,
  isInsideIOSWebKit,
  pushReroll,
  setParam,
} from "./utils";
import { setRecommendPlanById } from "../../utils/redux/slices/recommendedPlan";
import { TELEHEALTH_PLAN } from "src/funnels/payment-en/pages/telehealthRedirects";
import { getCurriculumVariationArm } from "src/utils/calculateGoals";
import { winbackRefresherCurriculum } from "src/utils/constants";
import { trackEvent } from "src/utils/api/tracker";
import { isBuyflow } from "./buyflow";

declare const Android: any;

// "/purchase/:planId"
// "/purchase/(en|es|de)/:planId"
// "/purchase-hm/:planId"
function isPurchase() {
  return ["payment", "payment-survey", "payment-checkout"].includes(
    getMeristemContext().context_type
  );
}

function purchaseRoot() {
  const state = getStore().getState();
  const { language, userData, geoLocation, recommendedPlan } = state;
  const routeId = getRouteId();

  const root = `/purchase${isHM(state) ? "-hm" : ""}`;

  const pathMatch = /^\/purchase(?:-hm)?(?:\/(?:en|es|de))?\/(.*)/.exec(
    location.pathname
  );

  const finalPlanId = recommendedPlan.noom_plan_id || pathMatch?.[1];

  /* istanbul ignore next sanity */
  if (!finalPlanId) {
    throw new Error("No plan id provided");
  }

  let purchaseUrl = isHM(state) ? `${root}/` : `${root}/${language || "en"}/`;
  purchaseUrl += finalPlanId;

  const queryParams = new URLSearchParams();

  // Add route to purchase page url params since cookies don't work 100% of the time.
  setParam(queryParams, "route", routeId);

  // Add fcc if exists, for the in-app buyflows, in order to enforce location based on device locale.
  if (geoLocation?.is_forced_country_code) {
    setParam(queryParams, "fcc", geoLocation.country_code);
  }

  const currentUrlParams = createForwardParams();
  currentUrlParams.forEach((value, key) => {
    queryParams.set(key, value);
  });

  if (isInApp()) {
    setParam(queryParams, "email", userData.email);
  }

  if (isDevMode()) {
    setParam(queryParams, "idm", true);
  }
  return `${purchaseUrl.replace(/\/$/, "")}/${getSearchQuery(queryParams)}`;
}

export default {
  async purchaseSurvey(planId?: string, question?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendPlanById(planId));
    }

    const pageSet = await getPageSetForLayer(
      "payment-survey",
      getStore().getState()
    );

    if (pageSet) {
      goto.layer("payment-survey");
      return;
    }

    // Fallback path for non-LBF, eg HM

    // Since we don't reroll going into payment funnel at this point,
    // we need to reset the context type to ensure we load the appropriate
    // server context on nav.
    if (isBuyflow()) {
      setContextType("payment-survey");
    }

    const url = `${purchaseRoot()}#/createAccount${
      question ? `/${question}` : ""
    }`;
    pushReroll(url, () => isPurchase());
  },

  async welcomeToNoom(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendPlanById(planId));
    }

    pushReroll(`${purchaseRoot()}#/welcomeToNoom`, isPurchase);
  },
  async account() {
    const pageSet = await getPageSetForLayer(
      "payment-checkout",
      getStore().getState()
    );
    if (pageSet) {
      // Account page is not in page set as it's been inlined. Just enter pageset
      await goto.pageSet(pageSet.id);
      return;
    }

    pushReroll(`${purchaseRoot()}#/account`, isPurchase);
  },
  async finalUpdateGraph(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendPlanById(planId));
    }

    pushReroll(`${purchaseRoot()}#/finalUpdateGraph`, isPurchase);
  },

  /* esde remove */ buildPlan() {
    pushReroll(`${purchaseRoot()}#/buildPlan`, isPurchase);
  },
  async chooseTrialPrice(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendPlanById(planId));
    }

    const pageSet = await getPageSetForLayer(
      "payment-checkout",
      getStore().getState()
    );
    if (pageSet) {
      // Account page is not in page set as it's been inlined. Just enter pageset
      await goto.pageSet(pageSet.id, "/payment/chooseTrialPrice");
      return;
    }

    pushReroll(`${purchaseRoot()}#/chooseTrialPrice`, isPurchase);
  },
  payment() {
    pushReroll(`${purchaseRoot()}#/payment`, isPurchase);
  },
  async paymentDetails(planId?: string) {
    if (planId) {
      await getStore().dispatch(setRecommendPlanById(planId));
    }

    pushReroll(`${purchaseRoot()}#/details`, isPurchase);
  },
  async telehealthPaymentDetails() {
    await getStore().dispatch(
      setRecommendPlanById(TELEHEALTH_PLAN, {
        overwriteForced: true,
      })
    );

    pushReroll(`${purchaseRoot()}#/details`, isPurchase);
  },
  programSwitchDetails() {
    pushReroll(`${purchaseRoot()}#/programSwitchDetails`, isPurchase);
  },
  programSwitchConfirmation() {
    pushReroll(`${purchaseRoot()}#/programSwitchConfirmation`, isPurchase);
  },
  stickToHWConfirmation() {
    pushReroll(`${purchaseRoot()}#/stickToHWConfirmation`, isPurchase);
  },

  nativePurchase() {
    if (isInsideIOSWebKit()) {
      try {
        // NOTE(norbert): the buyflowPurchaseNoDetails endpoint was only introduced
        // in the iOS 9.14 release, so we need to check if it exists before calling it
        if (
          isInAppNativePurchaseEligible() &&
          (window as any).webkit.messageHandlers.buyflowPurchaseNoDetails
        ) {
          // If app and route support web purchase, use that
          goto.buildPlan();
        } else {
          (window as any).webkit.messageHandlers.buyflowPurchase.postMessage(
            {}
          );
        }
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchase.ios");
      }
    }

    if (isInsideAndroidWebView()) {
      try {
        Android.buyflowPurchase();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchase.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchase: no message handler found");

    return false;
  },
  nativePurchaseNoDetails() {
    if (
      isInAppNativePurchaseEligible() &&
      (window as any).webkit?.messageHandlers?.buyflowPurchaseNoDetails
    ) {
      try {
        const curriculumVariationArm = getCurriculumVariationArm();
        if (curriculumVariationArm) {
          trackEvent("ForcedExperimentCurriculumChoice", {
            arm: curriculumVariationArm,
          });
          (
            window as any
          ).webkit.messageHandlers.buyflowPurchaseNoDetails.postMessage({
            forceExperimentsToAssign: [
              {
                name: winbackRefresherCurriculum.name,
                variation: curriculumVariationArm,
              },
            ],
          });
        } else {
          (
            window as any
          ).webkit.messageHandlers.buyflowPurchaseNoDetails.postMessage({});
        }
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseNoDetails.ios");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseNoDetails: no message handler found");

    return false;
  },
  nativePurchaseComplete() {
    if (isInsideIOSWebKit()) {
      try {
        (
          window as any
        ).webkit.messageHandlers.buyflowPurchaseCompleted.postMessage({});
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.ios");
      }
    }
    if (isInsideAndroidWebView()) {
      try {
        Android.buyflowPurchaseCompleted();
        return true;
      } catch (err) {
        // istanbul ignore next Error handling
        captureException(err, "nativePurchaseComplete.android");
      }
    }

    // eslint-disable-next-line no-console
    console.error("nativePurchaseComplete: no message handler found");
    return false;
  },
};
